import { ChangeEvent, FocusEvent, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { PATHS, useModal } from '@belong/common';
import { Text, Form, TextField, Spinner } from '@belong/ui';
import clsx from 'clsx';
import { AddressModel, NewOwnerOnboardingModel } from 'src/api/models';
import { trackEventService } from 'src/api/tracking';
import { AddressAutocomplete } from 'src/components/address-autocomplete/address-autocomplete';
import { INVALID_ZIPCODE } from 'src/constants/address';
import {
  HOMEOWNERS_STRINGS,
  HOMEOWNERS_STRINGS as STRINGS,
} from 'src/features/homeowners/homeowners.strings';
import {
  GA_ENTRY_POINTS,
  GA_EVENTS,
  GA_EXPERIMENT,
  QUERY_PARAMS,
} from 'src/features/rent-estimate/constants';
import { useUser, useMutateUser } from 'src/features/users/useUser';
import { NotThereYet } from 'src/modals/not-there-yet/not-there-yet';
import { useStore } from 'src/store';
import { replaceModalSelector } from 'src/store/modal';
import { GA_ACTIONS, trackHomeownerAddressEntered } from 'src/utils/tracking';
import { useCheckCoverage } from '../../hooks/use-check-coverage';
import { createProperties } from '../../queries';
import { QualifyModal } from '../qualify-modal/qualify-modal';

type FormAddress = AddressModel & { value: string };
type FormValues = {
  address: FormAddress;
  unit: string;
};

const initialValues: FormValues = {
  // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
  address: {
    value: '',
    streetAddress: '',
    city: '',
    state: '',
    zipcode: '',
  },
  unit: '',
};

type CalculatorSectionVariantProps = {
  open?: boolean;
};

export function CalculatorSectionVariant({
  open = false,
}: CalculatorSectionVariantProps) {
  const { user } = useUser();
  const { mutateAsync: fetchUser } = useMutateUser();
  const [unitFieldValue, setUnitFieldValue] = useState('');
  const [isAddressFieldDirty, setIsAddressFieldDirty] = useState(false);
  const selectedAddress = useRef<AddressModel>();
  const selectedUnitNumber = useRef<string>();
  const router = useRouter();
  const [isQualifyModalOpen, openQualifyModal, closeQualifyModal] =
    useModal(false);
  const [isNotThereYetModalOpen, openNotThereYetModal, closeNotThereYetModal] =
    useModal(false);
  const [isLoading, setIsLoading] = useState(false);
  const { checkCoverage, property } = useCheckCoverage(false);
  const replaceModal = useStore(replaceModalSelector);

  const handlePropertyCreation = async (
    properties: NewOwnerOnboardingModel['properties'],
  ) => {
    await createProperties({
      creationSource: 'RentEstimate',
      properties,
    });
  };

  async function handleSubmit(values: FormValues) {
    setIsLoading(true);

    const { address } = values;

    if (address.streetAddress === '') {
      await trackEventService({
        eventName: GA_EVENTS.FLOW_STARTED,
        metadata: {
          ...(user?.userId && { userId: user.userId }),
          variant: GA_ENTRY_POINTS.HO_LANDING_BANNER_CTA,
          experiment: GA_EXPERIMENT.RENT_ESTIMATE,
        },
      });

      router.push(PATHS.RENT_ESTIMATE_FLOW);

      return;
    }

    let units = [{}];

    if (address.unitNumber) {
      units = [{ unitNumber: address.unitNumber }];
    }

    if (unitFieldValue) {
      address.unitNumber = unitFieldValue;
    }

    const isInCoverage = await checkCoverage([address]);

    if (!isInCoverage) {
      setIsLoading(false);

      if (!user?.userId) {
        return openQualifyModal();
      }

      const newProperties = [
        {
          address,
          units,
        },
      ];

      // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
      await handlePropertyCreation(newProperties);

      return openNotThereYetModal();
    }

    trackHomeownerAddressEntered(
      GA_ACTIONS.ADDRESS_ENTERED_CALCULATOR,
      address,
    );

    await trackEventService({
      eventName: GA_EVENTS.FLOW_STARTED,
      metadata: {
        ...(user?.userId && { userId: user.userId }),
        variant: GA_ENTRY_POINTS.HO_LANDING_BANNER_CTA,
        experiment: GA_EXPERIMENT.RENT_ESTIMATE,
      },
    });

    router.push({
      pathname: PATHS.RENT_ESTIMATE_FLOW,
      query: {
        [QUERY_PARAMS.ADDRESS]: JSON.stringify(address),
      },
    });

    return;
  }

  function handleAddressValueChange(address: string) {
    setIsAddressFieldDirty(address !== '');
  }

  function handleAddressSelect(address: AddressModel) {
    if (address.zipcode === '') {
      address.zipcode = INVALID_ZIPCODE;
    }

    address.unitNumber = selectedUnitNumber.current;
    selectedAddress.current = address;
  }

  function onUnitBlur(event: FocusEvent<HTMLInputElement>) {
    const unitNumber = event.target.value?.trim();

    selectedUnitNumber.current = unitNumber;

    if (unitNumber === '') {
      // Avoid send empty string as a unitNumber (unitNumber='')
      selectedUnitNumber.current = undefined;
    }
  }

  async function handleQualifyModalLogin() {
    const freshUser = await fetchUser();

    window.geq?.suppress(freshUser?.email);

    closeQualifyModal();

    replaceModal({
      type: 'NOT_THERE_YET',
      props: { property, source: 'SignUpFlow' },
    });
  }

  return (
    <div
      className={clsx(
        'relative w-full',
        !open && 'border-gray py-xl rounded border border-solid',
      )}
    >
      {isLoading && <Spinner />}

      <Form initialValues={initialValues} onSubmit={handleSubmit}>
        {() => {
          return (
            <div className="px-xs w-full">
              <Text
                variant="h3"
                fontWeight="semibold"
                className="mb-sm text-center"
              >
                {STRINGS['doTheMath.title']}
              </Text>
              <div
                className={clsx(
                  'mb-lg gap-lg gap-x-2sm flex w-full flex-col justify-center lg:flex-row lg:items-end',
                  !open && 'mx-auto',
                )}
                style={open ? undefined : { maxWidth: '800px' }}
              >
                <div
                  className={clsx('duration-hover w-full transition-all', {
                    'lg:mb-0': isAddressFieldDirty,
                  })}
                >
                  <AddressAutocomplete
                    name="address"
                    label="Rental Home Address"
                    onAdressSelect={handleAddressSelect}
                    onValueChange={handleAddressValueChange}
                  />
                </div>

                <div
                  className={clsx(
                    'mb-2xl duration-hover hidden opacity-0 transition-all md:block',
                    isAddressFieldDirty
                      ? 'h-sm w-7xl opacity-100'
                      : 'pointer-events-none size-0',
                  )}
                >
                  <div className="inset-x-0 lg:contents">
                    <TextField
                      id="address-bar-unit"
                      name="unit"
                      label={HOMEOWNERS_STRINGS['cover.unitPlaceholder']}
                      value={unitFieldValue}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setUnitFieldValue(event.target.value)
                      }
                      onBlur={onUnitBlur}
                      focused
                    />
                  </div>
                </div>

                <div className="mb-2xl h-sm md:hidden">
                  <div className="inset-x-0">
                    <TextField
                      id="address-bar-unit-mobile"
                      name="unit"
                      label={HOMEOWNERS_STRINGS['cover.unitPlaceholder']}
                      value={unitFieldValue}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setUnitFieldValue(event.target.value)
                      }
                      onBlur={onUnitBlur}
                    />
                  </div>
                </div>

                <div className="mt-xl">
                  <Form.SubmitButton
                    size={{ _: 'fluid', lg: 'large' }}
                    variant="solid"
                    disabled={false}
                    className="relative"
                  >
                    {STRINGS['doTheMath.cta']}
                  </Form.SubmitButton>
                </div>
              </div>
            </div>
          );
        }}
      </Form>

      {property && (
        <NotThereYet
          isOpen={isNotThereYetModalOpen}
          onDismiss={closeNotThereYetModal}
          property={property}
        />
      )}

      <QualifyModal
        onDismiss={closeQualifyModal}
        isOpen={isQualifyModalOpen}
        onSuccessfulLogin={handleQualifyModalLogin}
        isLoading={isLoading}
      />
    </div>
  );
}
